import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {useEffect, useState} from "react";
import referrals from '../../models/referrals'
import helpers from "../../helpers/common";
import useRootHook from "../../hooks/useRoot";
import {Refresh, Visibility, Close} from "@mui/icons-material";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import {useSearchParams} from "react-router-dom";
import PageHeader from '../layout/PageHeader';
import SectionCard from '../layout/SectionCard';
import TableToolbar from '../layout/TableToolbar';
import {Box, Typography, Stack, TextField} from "@mui/material";
import {LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';

export default function ReferralStats() {
    const [searchParams, setSearchParams] = useSearchParams()
    const [refreshId, setRefreshId] = useState(0)
    const [statsData, setStatsData] = useState(null)
    const {setShowLoader, userData, setForcedMerchantForReferral, removeForcedMerchant} = useRootHook()
    const defaultCurrency = userData?.defaultCurrency

    useEffect(() => {
        setShowLoader(true)
        const filters = searchParams ? Object.fromEntries(searchParams) : {}
        // Set default date range if not provided
        if (!filters.from) {
            filters.from = moment().subtract(30, 'day').format('YYYY-MM-DD')
        }
        if (!filters.to) {
            filters.to = moment().format('YYYY-MM-DD')
        }
        referrals.getStats(filters)
            .then((data) => {
                data && setStatsData(data)
                setShowLoader(false)
            }).catch(() => setShowLoader(false))
    }, [searchParams, refreshId])

    const handleRefresh = () => {
        setRefreshId((prevId) => {
            return prevId + 1
        })
    }

    const handleDateChange = (field, date) => {
        const newParams = new URLSearchParams(searchParams)
        if (date) {
            newParams.set(field, moment(date).format('YYYY-MM-DD'))
        } else {
            newParams.delete(field)
        }
        setSearchParams(newParams)
    }

    if (!statsData) return null

    const totals = statsData.totals || {}
    const merchants = statsData.merchants || []
    const allowedMerchantIds = merchants.map((m) => m.merchant_id)

    return <React.Fragment>
        <Grid size={12}>
            <PageHeader
                title="Referral Statistics"
                breadcrumbs={[['Dashboard', '/'], ['Referral Stats', '/referrals/stats']]}
                actions={
                    <Stack direction="row" spacing={2}>
                        <Button
                            variant="outlined"
                            startIcon={<Close />}
                            onClick={() => {
                                removeForcedMerchant()
                                window.location.href = '/'
                            }}
                        >
                            Reset view
                        </Button>
                        <Button onClick={handleRefresh} variant="outlined" startIcon={<Refresh />}>
                            Refresh
                        </Button>
                    </Stack>
                }
            />
        </Grid>
        <Grid size={12}>
            <SectionCard>
                <Stack spacing={2} sx={{ mb: 3 }}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <DatePicker
                                label="From"
                                value={searchParams.get('from') ? moment(searchParams.get('from')) : moment().subtract(30, 'day')}
                                onChange={(date) => handleDateChange('from', date)}
                                renderInput={(params) => <TextField size="small" {...params} />}
                            />
                            <DatePicker
                                label="To"
                                value={searchParams.get('to') ? moment(searchParams.get('to')) : moment()}
                                onChange={(date) => handleDateChange('to', date)}
                                renderInput={(params) => <TextField size="small" {...params} />}
                            />
                        </Box>
                    </LocalizationProvider>
                </Stack>
            </SectionCard>
        </Grid>
        <Grid size={12}>
            <SectionCard>
                <Stack spacing={2} sx={{ mb: 3 }}>
                    <Typography variant="h6">Total Statistics</Typography>
                    <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <Box>
                            <Typography variant="body2" color="text.secondary">Total Earnings</Typography>
                            <Typography variant="h6">
                                {defaultCurrency ? helpers.getSubunitAmountString(totals.total_earnings || 0, defaultCurrency.symbol, defaultCurrency.subunit) : totals.total_earnings || 0}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" color="text.secondary">Total Amount</Typography>
                            <Typography variant="h6">
                                {defaultCurrency ? helpers.getSubunitAmountString(totals.total_amount || 0, defaultCurrency.symbol, defaultCurrency.subunit) : totals.total_amount || 0}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" color="text.secondary">Total Transactions</Typography>
                            <Typography variant="h6">{totals.total_transactions || 0}</Typography>
                        </Box>
                    </Box>
                </Stack>
            </SectionCard>
        </Grid>
        <Grid size={12}>
            <SectionCard sx={{ display: 'flex', flexDirection: 'column', overflowX: 'auto'}}>
                <TableToolbar title="Merchants Statistics" />
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Merchant</TableCell>
                            <TableCell>Username</TableCell>
                            <TableCell align="right">Percentage</TableCell>
                            <TableCell align="right">Total Amount</TableCell>
                            <TableCell align="right">Transactions</TableCell>
                            <TableCell align="right">Your Earnings</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {merchants.map((row) => (
                            <TableRow key={`merchant-${row.merchant_id}`}>
                                <TableCell>{row.merchant_name}</TableCell>
                                <TableCell>{row.merchant_username}</TableCell>
                                <TableCell align="right">{row.percentage}%</TableCell>
                                <TableCell align="right">
                                    {defaultCurrency ? helpers.getSubunitAmountString(row.total_amount || 0, defaultCurrency.symbol, defaultCurrency.subunit) : row.total_amount || 0}
                                </TableCell>
                                <TableCell align="right">{row.transaction_count || 0}</TableCell>
                                <TableCell align="right">
                                    {defaultCurrency ? helpers.getSubunitAmountString(row.referral_earnings || 0, defaultCurrency.symbol, defaultCurrency.subunit) : row.referral_earnings || 0}
                                </TableCell>
                                <TableCell align="right">
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<Visibility />}
                                        onClick={() => {
                                            setForcedMerchantForReferral(row.merchant_id, allowedMerchantIds)
                                            window.location.href = '/?force_merchant_id=' + row.merchant_id
                                        }}
                                    >
                                        View as merchant
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                        {merchants.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    No merchants assigned
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </SectionCard>
        </Grid>
    </React.Fragment>
}

